import { LuIcon, Menu, type MenuGroupProps } from "@overmap-ai/blocks"
import { OrganizationAccessLevel } from "@overmap-ai/common"
import { useNavigate } from "@tanstack/react-router"
import type { Position } from "geojson"
import { LngLat } from "mapbox-gl"
import { memo, useCallback } from "react"
import { useMap } from "react-map-gl/mapbox"

import { VIEW_ON_MAP_ZOOM } from "../../mapbox/constants"
import { openCoordsInGoogleMaps } from "../../mapbox/utils"
import { modelIconMapping } from "../../mappings/icons"
import { OrganizationAccessRequired } from "../OrganizationAccessRequired/AccessRequired"

export interface LocationGroupProps extends MenuGroupProps {
	onLocationChange?: () => void
	coordinates: Position | undefined
}

export const LocationGroup = memo<LocationGroupProps>((props) => {
	const { ref, onLocationChange, coordinates, ...rest } = props
	const { current: map } = useMap()
	const navigate = useNavigate()

	const handleViewLocationOnMap = useCallback(() => {
		if (!coordinates) return

		void navigate({ from: "/$organizationId/$projectId", to: "/$organizationId/$projectId" }).then(() => {
			map?.flyTo({
				center: new LngLat(coordinates[0], coordinates[1]),
				zoom: VIEW_ON_MAP_ZOOM,
				duration: 0.2,
			})
		})
	}, [coordinates, map, navigate])

	const handleOpenCoordsInGoogleMaps = useCallback(() => {
		if (!coordinates) return

		openCoordsInGoogleMaps(coordinates)
	}, [coordinates])

	return (
		<Menu.Group ref={ref} {...rest}>
			{!!onLocationChange && (
				<OrganizationAccessRequired accessLevel={OrganizationAccessLevel.BASIC} type="hide">
					<Menu.Item onSelect={onLocationChange}>
						<LuIcon icon={modelIconMapping.location} />
						Change location
					</Menu.Item>
				</OrganizationAccessRequired>
			)}
			<Menu.Item disabled={!coordinates} onSelect={handleViewLocationOnMap}>
				<LuIcon icon={modelIconMapping.navigate} />
				Travel to location
			</Menu.Item>
			<Menu.Item onSelect={handleOpenCoordsInGoogleMaps} disabled={!coordinates}>
				<LuIcon icon={modelIconMapping.externalLink} />
				View in google maps
			</Menu.Item>
		</Menu.Group>
	)
})
LocationGroup.displayName = "LocationGroup"
